import { apiClient, type PaginatedResponse, APIError } from '../../../api/client'
import { GeneralfilesApi } from '../../../generated-types/apis/GeneralfilesApi'
import { Configuration } from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import { type FileMetadata, type Folder } from '../utils/types'
import { type FileInfo } from '../../../utils/types'
import { createFileInfo } from '../../../utils/functions'
import buildApiUrl, { type PaginationOptions } from '../../../api/pagination'
import { type GenericFilterType, type PaginatedQueryParameters } from '../../../api/hooks/utils/types'

const log = new LogTool({ context: 'FilesystemAPI', enable: true, logLevel: 'warn' })

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      return token || ''
    }
  })
}

/**
 * Create API instance
 */
const filesApi = new GeneralfilesApi(getApiConfiguration())

export interface FilesystemResponse {
  directories: Folder[]
  files: FileMetadata[]
}

/**
 * Fetch files in a folder
 * Note: Uses apiClient for custom query parameter handling
 */
export async function fetchFilesInFolder(path = ''): Promise<FilesystemResponse> {
  log.info('Fetching files in folder:', path)

  const response = await apiClient.get<FilesystemResponse>(`v1/general/files/?path=${path}`)
  return response
}

/**
 * Download a file
 * Note: Uses apiClient for blob download
 */
export async function downloadFile(filePath: string): Promise<Blob> {
  log.info('Downloading file:', filePath)

  const response = await apiClient.downloadBlob(`v1/general/files${filePath}`)
  return response
}

/**
 * Delete a file
 * Note: Uses apiClient for custom path-based deletion
 */
export async function deleteFile(filePath: string): Promise<void> {
  log.info('Deleting file:', filePath)

  const isAbsoluteUrl = /^https?:\/\//i.test(filePath)
  const isApiPath = filePath.startsWith('/v1/') || filePath.startsWith('v1/')
  const target = (isAbsoluteUrl || isApiPath)
    ? filePath
    : `v1/general/files${filePath}`

  await apiClient.delete(target)
}

/**
 * Delete a folder
 * Note: Uses apiClient for custom path-based deletion
 */
export async function deleteFolder(folderPath: string): Promise<void> {
  log.info('Deleting folder:', folderPath)

  // Ensure folder path ends with /
  const path = folderPath.endsWith('/') ? folderPath : `${folderPath}/`
  await apiClient.delete(`v1/general/files${path}`)
}

/**
 * Upload a file
 * Note: Uses apiClient for multipart/form-data upload
 */
export async function uploadFile(
  path: string,
  file: File // Native browser File type
): Promise<any> {
  log.info('Uploading file:', file.name, 'to path:', path)

  const formData = new FormData()
  formData.append('file', file)
  formData.append('path', path)

  const response = await apiClient.post<any>('v1/general/files/', formData, undefined, true)
  return response
}

/**
 * Create a folder
 * Note: Folders are created by creating a file path that ends with "/"
 * Uses apiClient for multipart/form-data with custom path handling
 */
export async function createFolder(
  path: string,
  folderName: string
): Promise<any> {
  log.info('Creating folder:', folderName, 'in path:', path)

  const formData = new FormData()
  const folderPath = path + '/' + folderName + '/'
  formData.append('path', folderPath)

  const response = await apiClient.post<any>('v1/general/files/', formData, undefined, true)
  return response
}

/**
 * Fetch file list from a specific URL (supports pagination)
 * Note: This is for fetching file lists from URLs like article files, contract files, etc.
 */
export async function fetchFileList(
  url: string,
  options: PaginatedQueryParameters<FileInfo, GenericFilterType> | PaginationOptions = {}
): Promise<PaginatedResponse<FileInfo>> {
  log.info('Fetching file list from:', url)

  // Check if the url leads to a correct API endpoint
  if (!url.match(/files\//)) {
    throw new Error(`The provided URL ${url} does not lead to the correct API endpoint!`)
  }

  const fetchUrl = buildApiUrl(
    url,
    options as PaginatedQueryParameters<FileInfo, GenericFilterType>
  )
  const response = await apiClient.get<PaginatedResponse<any>>(fetchUrl)

  const files = response.results.map((fileResponse: unknown) => createFileInfo(fileResponse))

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: files,
  }
}

/**
 * Upload a file to a specific URL endpoint
 * Note: This is for uploading files to specific resource endpoints (articles, contracts, etc.)
 */
export async function uploadFileToUrl(
  url: string,
  file: File
): Promise<FileInfo> {
  log.info('Uploading file to URL:', url, 'File:', file.name)

  // Check if the url leads to an API endpoint that is intended to accept files
  if (!(url.match(/files\//) || url.match(/import/))) {
    throw new Error(
      `The provided URL ${url} does not lead to an API endpoint that is intended to accept file uploads!`
    )
  }

  // Create FormData object to make file ready for upload
  const fileFormData: FormData = new FormData()
  fileFormData.append('document', file)

  const response = await apiClient.post<any>(url, fileFormData, {
    Accept: '*/*',
  }, true)

  return createFileInfo(response)
}

/**
 * Delete a file from a specific URL
 * Enhanced to support both path-based and URL-based deletion
 */
export async function deleteFileFromUrl(url: string): Promise<void> {
  log.info('Deleting file from URL:', url)

  // Check if the url leads to an API endpoint that holds any files
  if (!url.match(/\/files\//)) {
    throw new Error(`The provided URL ${url} does not lead to an API endpoint that holds any files!`)
  }

  try {
    await apiClient.delete(url)
  } catch (error) {
    // Handle 403 Forbidden errors with user-friendly message
    if (error instanceof APIError && error.status === 403) {
      throw new Error(
        'You do not have permission to delete this file. Staff users cannot delete article files from other organizations that are used in requests.'
      )
    }
    // Re-throw other errors as-is
    throw error
  }
}
